import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Loading from '../../components/Loading';
import Textarea from '../../components/Textarea';
import ScoreSlider from '../../components/ScoreSlider';
import { getSubmissionForReview, createReview, getReviewerMyReview } from '../../utils/api';
import { fetchAsBlobUrl, downloadPdfFile, extractFilename, viewPdfInNewTab } from '../../utils/pdfHelper';

const ReviewPaper = () => {
  const { submissionId, id } = useParams();
  const actualSubmissionId = submissionId || id;
  const navigate = useNavigate();

  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Rubric modal state
  const [showRubricModal, setShowRubricModal] = useState(false);
  const [rubricPdfBlobUrl, setRubricPdfBlobUrl] = useState(null);
  const [rubricPdfLoading, setRubricPdfLoading] = useState(false);

  // Review status state
  const [reviewStatus, setReviewStatus] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const [formData, setFormData] = useState({
    score: 5,
    recommendation: '',
    comments: '',
    confidentialComments: ''
  });

  // PDF helper functions imported from utils/pdfHelper

  const recommendations = [
    { value: 'ACCEPT', label: 'Accept', color: 'text-green-600' },
    { value: 'MINOR_REVISION', label: 'Minor Revision', color: 'text-yellow-600' },
    { value: 'MAJOR_REVISION', label: 'Major Revision', color: 'text-orange-600' },
    { value: 'REJECT', label: 'Reject', color: 'text-red-600' }
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch submission and review status in parallel
      const [submissionRes, reviewRes] = await Promise.all([
        getSubmissionForReview(actualSubmissionId),
        getReviewerMyReview(actualSubmissionId)
      ]);

      setSubmission(submissionRes.data || submissionRes);

      const reviewData = reviewRes.data || reviewRes;
      setReviewStatus(reviewData);

      // If reviewer has an existing review
      if (reviewData?.hasReview) {
        const review = reviewData.review;

        // Pre-fill form with previous review data
        setFormData({
          score: review.score || 5,
          recommendation: review.recommendation || '',
          comments: review.comments || '',
          confidentialComments: review.confidentialComments || ''
        });

        // Set update mode if canUpdate is true
        if (reviewData.canUpdate) {
          setIsUpdateMode(true);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to load submission');
    } finally {
      setLoading(false);
    }
  }, [actualSubmissionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load PDF blob when modal opens
  useEffect(() => {
    if (showPdfModal && submission?.fileUrl && !pdfBlobUrl) {
      setPdfLoading(true);
      fetchAsBlobUrl(submission.fileUrl)
        .then(url => {
          setPdfBlobUrl(url);
          setPdfLoading(false);
        })
        .catch(err => {
          console.error('Error loading PDF:', err);
          setPdfLoading(false);
        });
    }
  }, [showPdfModal, submission?.fileUrl, pdfBlobUrl]);

  // Cleanup blob URL when modal closes
  useEffect(() => {
    if (!showPdfModal && pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  }, [showPdfModal, pdfBlobUrl]);

  // Load Rubric PDF blob when rubric modal opens
  useEffect(() => {
    const rubricUrl = submission?.trackId?.rubricUrl || submission?.conferenceId?.defaultRubricUrl;
    const rubricName = submission?.trackId?.rubricOriginalName || submission?.conferenceId?.defaultRubricOriginalName;
    const isDoc = rubricName && (rubricName.toLowerCase().endsWith('.doc') || rubricName.toLowerCase().endsWith('.docx'));

    if (showRubricModal && rubricUrl && !isDoc && !rubricPdfBlobUrl) {
      setRubricPdfLoading(true);
      fetchAsBlobUrl(rubricUrl)
        .then((url) => {
          setRubricPdfBlobUrl(url);
          setRubricPdfLoading(false);
        })
        .catch((err) => {
          console.error('Error loading rubric PDF:', err);
          setRubricPdfLoading(false);
        });
    }
  }, [showRubricModal, submission, rubricPdfBlobUrl]);

  useEffect(() => {
    if (!showRubricModal && rubricPdfBlobUrl) {
      window.URL.revokeObjectURL(rubricPdfBlobUrl);
      setRubricPdfBlobUrl(null);
    }
  }, [showRubricModal, rubricPdfBlobUrl]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const errors = {};
    if (!formData.recommendation) {
      errors.recommendation = 'Please select a recommendation';
    }
    if (!formData.comments.trim()) {
      errors.comments = 'Please provide comments for the author';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please resolve all validation errors before submitting');
      setTimeout(() => {
        const firstErrorEl = document.querySelector('.border-red-500');
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorEl.focus?.();
        }
      }, 100);
      return;
    }

    setSubmitting(true);
    try {
      await createReview(actualSubmissionId, {
        score: Number(formData.score),
        recommendation: formData.recommendation,
        comments: formData.comments,
        confidentialComments: formData.confidentialComments
      });
      navigate('/reviewer/reviews');
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Loading fullScreen message="Loading submission..." />
      </>
    );
  }

  // Block access if final verdict (ACCEPT/REJECT) was already submitted
  if (reviewStatus?.hasReview && reviewStatus?.isFinalVerdict) {
    return (
      <>
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              ← Back
            </Button>
          </div>

          <Card className="text-center py-12">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Review Already Submitted</h2>
            <p className="text-gray-600 mb-2">
              You have already submitted your final review for this paper.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg mt-4">
              <span className="font-medium">Your Verdict:</span>
              <Badge variant={reviewStatus.review?.recommendation === 'ACCEPT' ? 'success' : 'danger'}>
                {reviewStatus.review?.recommendation}
              </Badge>
            </div>
            <div className="mt-6">
              <Button onClick={() => navigate('/reviewer/bids')}>
                Back to My Bids
              </Button>
            </div>
          </Card>
        </div>
      </>
    );
  }

  // Block access if review with revision verdict exists but author hasn't resubmitted yet
  if (reviewStatus?.hasReview && !reviewStatus?.isFinalVerdict && !reviewStatus?.canUpdate) {
    return (
      <>
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              ← Back
            </Button>
          </div>

          <Card className="text-center py-12">
            <div className="text-6xl mb-4">⏳</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Waiting for Author Revision</h2>
            <p className="text-gray-600 mb-2">
              You requested a revision for this paper. The author has not yet submitted their revised version.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-lg mt-4 border border-orange-200">
              <span className="font-medium text-orange-800">Your Previous Verdict:</span>
              <Badge variant="warning">
                {reviewStatus.review?.recommendation?.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              You will be able to update your review once the author submits their revision.
            </p>
            <div className="mt-6">
              <Button onClick={() => navigate('/reviewer/bids')}>
                Back to My Bids
              </Button>
            </div>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            ← Back
          </Button>
        </div>

        {/* Update mode banner */}
        {isUpdateMode && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-blue-800">Update Your Review</h3>
                <p className="text-sm text-blue-700">
                  The author has submitted a revised version. Please review the updated paper and submit your new evaluation.
                </p>
              </div>
            </div>
          </div>
        )}

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
          {isUpdateMode ? 'Update Review' : 'Write Review'}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
          {isUpdateMode ? 'Review the revised paper and update your evaluation' : 'Review this paper submission'}
        </p>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Submission Details */}
        {submission && (
          <Card className="mb-8">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {submission.title}
                </h2>
                <div className="flex flex-wrap gap-2 mb-3">
                  {submission.conferenceId?.name && (
                    <Badge variant="info">{submission.conferenceId.name}</Badge>
                  )}
                  {submission.trackId?.name && (
                    <Badge variant="default">{submission.trackId.name}</Badge>
                  )}
                  {submission.theme && (
                    <Badge variant="default">Theme: {submission.theme}</Badge>
                  )}
                </div>
              </div>
            </div>

            {submission.abstract && (
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">Abstract</h3>
                <p className="text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-lg">
                  {submission.abstract}
                </p>
              </div>
            )}

            {submission.keywords && submission.keywords.length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">Keywords</h3>
                <div className="flex flex-wrap gap-2">
                  {submission.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {submission.fileUrl && (
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowPdfModal(true)}
                  className="hidden"
                >
                  Preview Paper
                </Button>
                <button
                  onClick={() => downloadPdfFile(submission.fileUrl, extractFilename(submission.fileUrl, submission.title))}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download PDF
                </button>
              </div>
            )}
          </Card>
        )}

        {/* Evaluation Rubrics Banner */}
        {(() => {
          const activeRubricUrl = submission?.trackId?.rubricUrl || submission?.conferenceId?.defaultRubricUrl;
          const activeRubricName = submission?.trackId?.rubricOriginalName || submission?.conferenceId?.defaultRubricOriginalName || 'Evaluation Rubric Document';
          const isTrackSpecific = Boolean(submission?.trackId?.rubricUrl);

          if (!activeRubricUrl) return null;

          return (
            <div className="mb-8 p-5 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-3.5">
                  <div className="p-2.5 bg-indigo-600 text-white rounded-lg shadow-sm mt-0.5">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-bold text-gray-900">
                        Evaluation Rubric
                      </h3>
                      {isTrackSpecific && submission?.trackId?.name && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                          {submission.trackId.name}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Please score and evaluate this paper according to the official criteria: <span className="font-semibold text-gray-800">{activeRubricName}</span>
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 flex-shrink-0 self-end sm:self-center">
                  <button
                    type="button"
                    onClick={() => setShowRubricModal(true)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-indigo-700 bg-white hover:bg-indigo-50 border border-indigo-200 rounded-lg shadow-sm transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Rubric
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadPdfFile(activeRubricUrl, extractFilename(activeRubricUrl, activeRubricName))}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Review Form */}
        <Card>
          <h2 className="text-xl font-bold text-gray-900 mb-6">Your Review</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Score */}
            <div>
              <ScoreSlider
                value={formData.score}
                onChange={(val) => handleInputChange('score', val)}
                min={1}
                max={10}
                label="Score (1-10)"
              />
            </div>

            {/* Recommendation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recommendation <span className="text-red-500">*</span>
              </label>
              <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 p-1 rounded-lg border transition-all duration-200 ${fieldErrors.recommendation ? 'border-red-500' : 'border-transparent'}`}>
                {recommendations.map((rec) => (
                  <button
                    key={rec.value}
                    type="button"
                    onClick={() => {
                      handleInputChange('recommendation', rec.value);
                      setFieldErrors(prev => ({ ...prev, recommendation: '' }));
                    }}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${formData.recommendation === rec.value
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <span className={`font-medium ${rec.color}`}>{rec.label}</span>
                  </button>
                ))}
              </div>
              {fieldErrors.recommendation && <p className="mt-1 text-sm text-red-500">{fieldErrors.recommendation}</p>}
            </div>

            {/* Comments for Author */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comments for Author <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={formData.comments}
                onChange={(e) => {
                  handleInputChange('comments', e.target.value);
                  setFieldErrors(prev => ({ ...prev, comments: '' }));
                }}
                placeholder="Provide detailed feedback for the author. This will be visible to them."
                rows={6}
                error={fieldErrors.comments}
              />
              <p className="text-xs text-gray-500 mt-1">
                Be constructive and specific. This feedback will be shared with the author.
              </p>
            </div>

            {/* Confidential Comments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confidential Comments (Optional)
              </label>
              <Textarea
                value={formData.confidentialComments}
                onChange={(e) => handleInputChange('confidentialComments', e.target.value)}
                placeholder="Private notes for the organizer only. The author will not see these."
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">
                These comments are only visible to organizers.
              </p>
            </div>

            {/* Submit */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-850 font-medium text-sm">{error}</p>
              </div>
            )}
            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                fullWidth
                disabled={submitting}
                className={submitting ? 'opacity-50 cursor-not-allowed' : ''}
              >
                {submitting
                  ? (isUpdateMode ? 'Updating...' : 'Submitting...')
                  : (isUpdateMode ? 'Update Review' : 'Submit Review')
                }
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>

      {/* PDF Preview Modal */}
      {showPdfModal && submission?.fileUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {submission.title}
              </h3>
              <button
                onClick={() => setShowPdfModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div style={{ height: '70vh' }} className="bg-gray-100 flex items-center justify-center">
              {pdfLoading ? (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading PDF...</p>
                </div>
              ) : pdfBlobUrl ? (
                <iframe
                  src={pdfBlobUrl}
                  className="w-full h-full"
                  title="Paper Preview"
                />
              ) : (
                <div className="text-center text-gray-500">
                  <p>Unable to load PDF preview</p>
                  <button
                    onClick={() => viewPdfInNewTab(submission.fileUrl)}
                    className="mt-2 text-blue-600 hover:text-blue-700"
                  >
                    Open in New Tab instead
                  </button>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-between items-center">
              <div className="flex gap-3">
                <button
                  onClick={() => viewPdfInNewTab(submission.fileUrl)}
                  className="hidden px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-lg transition-colors"
                >
                  📄 Open in New Tab
                </button>
                <button
                  onClick={() => downloadPdfFile(submission.fileUrl, extractFilename(submission.fileUrl, submission.title))}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download PDF
                </button>
              </div>
              <Button variant="outline" onClick={() => setShowPdfModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rubric Preview Modal */}
      {showRubricModal && (() => {
        const rubricUrl = submission?.trackId?.rubricUrl || submission?.conferenceId?.defaultRubricUrl;
        const rubricName = submission?.trackId?.rubricOriginalName || submission?.conferenceId?.defaultRubricOriginalName || 'Evaluation Rubric Document';

        return (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
              <div className="flex justify-between items-center px-6 py-4 border-b">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 truncate max-w-xl">
                    {rubricName}
                  </h3>
                </div>
                <button
                  onClick={() => setShowRubricModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div style={{ height: '65vh' }} className="bg-gray-100 flex items-center justify-center">
                {rubricPdfLoading ? (
                  <div className="text-center">
                    <svg className="animate-spin h-10 w-10 text-indigo-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-sm text-gray-600 font-medium">Loading Rubric PDF...</p>
                  </div>
                ) : rubricPdfBlobUrl ? (
                  <iframe
                    src={rubricPdfBlobUrl}
                    className="w-full h-full border-0"
                    title="Rubric Preview"
                  />
                ) : (
                  <div className="text-center p-8 text-gray-600">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="font-medium text-gray-800">Preview not supported in-browser for this document.</p>
                    <p className="text-xs text-gray-500 mt-1 mb-4">Please download the file to inspect the evaluation rubrics.</p>
                    <button
                      onClick={() => downloadPdfFile(rubricUrl, extractFilename(rubricUrl, rubricName))}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Rubric Document
                    </button>
                  </div>
                )}
              </div>

              <div className="px-6 py-3 border-t bg-gray-50 flex justify-between items-center">
                <button
                  onClick={() => downloadPdfFile(rubricUrl, extractFilename(rubricUrl, rubricName))}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 border border-indigo-200 hover:border-indigo-400 rounded-lg bg-white transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Rubric
                </button>
                <Button variant="outline" size="sm" onClick={() => setShowRubricModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default ReviewPaper;
